import type { Announcement } from "@amazing-care/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDocuments } from "@/features/documents/use-documents";
import { dataSource } from "@/lib/data-source";
import { isWithinDays } from "@/lib/dates";
import { queryKeys } from "@/lib/query-keys";
import { ANNOUNCEMENT_RECENT_DAYS } from "./staff-home.constants";

/** Documents older than this many days no longer count as a recent update. */
const RECENT_UPDATE_WINDOW_DAYS = 14;

/** Posted within the window the home page shows by default. */
export function isRecentAnnouncement(announcement: Announcement, now = Date.now()): boolean {
  return isWithinDays(announcement.timestamp, ANNOUNCEMENT_RECENT_DAYS, now);
}

export function useAnnouncements() {
  return useQuery({
    queryKey: queryKeys.announcements,
    queryFn: () => dataSource.listAnnouncements(),
  });
}

export function useReadAnnouncementIds() {
  return useQuery({
    queryKey: queryKeys.readAnnouncements,
    queryFn: () => dataSource.listReadAnnouncementIds(),
    select: (ids) => new Set(ids),
  });
}

/**
 * Read state is updated in the cache before the server answers, so the badge
 * and the "New" marks clear on click. A failure puts the old state back.
 */
function useOptimisticReadIds<TVariables>(
  mutationFn: (variables: TVariables) => Promise<void>,
  nextReadIds: (current: string[], variables: TVariables) => string[]
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    meta: { silent: true },
    onMutate: async (variables: TVariables) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.readAnnouncements });
      const previous = queryClient.getQueryData<string[]>(queryKeys.readAnnouncements);
      queryClient.setQueryData<string[]>(queryKeys.readAnnouncements, (current = []) =>
        nextReadIds(current, variables)
      );
      return { previous };
    },
    onError: (_error, _variables, context) => {
      queryClient.setQueryData(queryKeys.readAnnouncements, context?.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.readAnnouncements }),
  });
}

export function useMarkAnnouncementRead() {
  return useOptimisticReadIds(
    (id: string) => dataSource.markAnnouncementRead(id),
    (current, id) => (current.includes(id) ? current : [...current, id])
  );
}

export function useMarkAllAnnouncementsRead() {
  const queryClient = useQueryClient();
  return useOptimisticReadIds(
    () => dataSource.markAllAnnouncementsRead(),
    (current) => {
      const announcements = queryClient.getQueryData<Announcement[]>(queryKeys.announcements);
      return Array.from(new Set([...current, ...(announcements ?? []).map(({ id }) => id)]));
    }
  );
}

/**
 * Unread count, shared by the sidebar badge and the announcements header. Only
 * recent announcements count, so the badge never points at one that is hidden.
 */
export function useUnreadAnnouncementCount(): number {
  const { data: announcements } = useAnnouncements();
  const { data: readIds } = useReadAnnouncementIds();
  if (!announcements || !readIds) return 0;
  const now = Date.now();
  return announcements.filter(
    (announcement) => isRecentAnnouncement(announcement, now) && !readIds.has(announcement.id)
  ).length;
}

export function useActivityLog() {
  return useQuery({
    queryKey: queryKeys.activityLog,
    queryFn: () => dataSource.listActivityLog(),
  });
}

/**
 * Documents touched within the window that ends at the most recent update,
 * matching the prototype's "Recent updates" tile.
 */
export function useRecentUpdateCount(): number {
  const { data: documents } = useDocuments();
  if (!documents || documents.length === 0) return 0;
  const newest = Math.max(...documents.map((d) => new Date(d.lastUpdated).getTime()));
  return documents.filter((d) => isWithinDays(d.lastUpdated, RECENT_UPDATE_WINDOW_DAYS, newest))
    .length;
}
